/**
 * The portfolio: the organisation's business areas, each a section heading with a line of what it holds (its
 * projects, the people on them, its month's spend, what waits on its managers) and its projects as the same cards
 * the Projects list draws; the filing requests waiting, decided in place; and the projects under no area. All from
 * one answer, `GET /projects/v1/overview`. New areas and projects, editing an area and moving a project are short
 * dialogs. Teams and specialists live on the Teams page; the portfolio links there.
 */

import { formatMoney, type AreaManager, type ProjectOverview, type ProjectOverviewArea, type ProjectOverviewCard, type ProjectOverviewPromotion } from '@aioe/shared';
import { ArrowUpFromLine, Briefcase, Folder, Persons, Plus, Pulse } from '@gravity-ui/icons';
import { Button, Skeleton } from '@heroui/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { FormModal } from '../components/cloud/FormModal.tsx';
import { areaTone, MoveProjectDialog, NewProjectDialog, ProjectGrid } from '../components/projectCards.tsx';
import { SubjectName, SubjectPicker } from '../components/SubjectPicker.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { useStepUp } from '../components/rolesPermissions.tsx';
import { groupsAvailable } from '../directory.ts';
import { useMay } from '../permissions.ts';
import { ErrorNote, inputClass, ListGroup, ListRow, Page, SectionHeading, StatusMark } from '../components/ui.tsx';
import { ArtEmpty, ArtStat, statNumber, VIEW_TONES, ViewHeader } from '../components/ui/Art.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { listCount, NoMatch, ShowMore, useListSearch } from '../pagedList.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';

export function PortfolioPage() {
  const tone = VIEW_TONES.portfolio!;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const load = useLoad(() => routes.projectOverview(), []);
  const whole = load.data;
  // A search over the projects and the areas: the answer is cut to what matches, and a long area draws a page of cards at a time.
  const search = useListSearch(150);
  const words = search.typed.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const hit = (...fields: (string | undefined)[]) => words.every((w) => fields.some((f) => f?.toLowerCase().includes(w)));
  const wordsKey = words.join(' ');
  const o: ProjectOverview | undefined = useMemo(() => (whole && words.length ? { ...whole, projects: whole.projects.filter((p) => hit(p.name, p.id, p.purpose, p.area?.name, ...p.machines.names)) } : whole), [whole, wordsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const shownAreas = o ? o.areas.filter((a) => !words.length || hit(a.name, a.description) || o.projects.some((p) => p.area?.id === a.id)) : [];
  const [creating, setCreating] = useState<{ area?: string }>();
  const [newArea, setNewArea] = useState(false);
  const [editing, setEditing] = useState<ProjectOverviewArea>();
  const [moving, setMoving] = useState<ProjectOverviewCard>();
  const filing = useRef<HTMLDivElement>(null);
  // The old tabs' links: Teams moved to its own page; filing requests are on this one.
  const tab = params.get('tab');
  useEffect(() => {
    if (tab === 'teams') navigate('/teams', { replace: true });
  }, [tab, navigate]);
  useEffect(() => {
    if (tab === 'promotions' && o) filing.current?.scrollIntoView({ block: 'start' });
  }, [tab, o]);

  const [unfiledDrawn, setUnfiledDrawn] = useState(AREA_PAGE);
  const unfiled = o ? o.projects.filter((p) => !p.area) : [];
  const mayMove = (c: ProjectOverviewCard) => Boolean(o && (o.can.portfolioManager || (o.can.manages.length > 0 && (!c.area || o.can.manages.includes(c.area.id)))));
  const decides = o?.promotions.filter((p) => p.decides).length ?? 0;
  const spend = o ? o.areas.reduce((n, a) => n + (a.spendUsd ?? 0), 0) : 0;

  return (
    <Page>
      <ViewHeader
        tone={tone}
        icon={<Briefcase />}
        title="Portfolio"
        blurb="The organisation's business areas, the projects each one owns, and what waits on their managers."
        actions={
          <>
            {o?.can.portfolioManager ? (
              <Button size="sm" variant="secondary" onPress={() => setNewArea(true)}>
                New business area
              </Button>
            ) : null}
            {o?.can.create ? (
              <Button size="sm" variant="primary" onPress={() => setCreating({})}>
                <Plus className="size-4" />
                New project
              </Button>
            ) : null}
          </>
        }
      />
      <ErrorNote error={load.error} />
      {!o && load.loading ? <Skeleton className="h-40 rounded-2xl" /> : null}
      {o ? (
        <>
          <div className="art-stats">
            <ArtStat tone="violet" motif="rings" icon={<Briefcase />} label="Business areas" value={statNumber(o.areas.length)} detail={o.can.portfolioManager ? 'You manage every area' : o.can.manages.length ? `You manage ${o.can.manages.length}` : 'You manage none'} />
            <ArtStat tone="blue" motif="arc" icon={<Folder />} label="Projects" value={statNumber(o.projects.length)} detail={unfiled.length ? `${unfiled.length} not in an area` : 'Every one in an area'} />
            <ArtStat tone="amber" motif="rings" icon={<ArrowUpFromLine />} label="Filing requests" value={statNumber(o.promotions.length)} detail={decides ? `${decides} waiting on you` : o.promotions.length ? 'Waiting on an area’s managers' : 'Nothing waiting'} needsYou={decides > 0} {...(o.promotions.length ? { onPress: () => filing.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) } : {})} />
            <ArtStat tone="green" motif="arc" icon={<Pulse />} label="Spend, 30 days" value={formatMoney(spend, o.money, { compact: true })} detail={spend > 0 ? 'Across the areas' : 'Nothing booked yet'} />
          </div>

          {o.promotions.length ? (
            <div ref={filing} className="scroll-mt-6">
              <PartBoundary key="filing" name="Filing requests" tone={tone}>
                <FilingRequests overview={o} onChange={load.reload} />
              </PartBoundary>
            </div>
          ) : null}

          {o.areas.length === 0 ? (
            <ArtEmpty
              tone={tone}
              icon={<Briefcase />}
              title="No business areas yet"
              action={
                o.can.portfolioManager ? (
                  <Button size="sm" variant="primary" onPress={() => setNewArea(true)}>
                    New business area
                  </Button>
                ) : undefined
              }
            >
              A business area is a part of the organisation that owns projects, with the people who manage them and the cost code its spend is booked to.
            </ArtEmpty>
          ) : null}

          {whole && whole.projects.length > 6 ? (
            <div className="flex flex-wrap items-center gap-3">
              <SearchBox label="Search the portfolio" value={search.typed} onChange={search.setTyped} placeholder="Search projects and areas" className="w-72 max-w-full" />
              {words.length ? <span className="text-sm text-muted">{listCount(o.projects.length, { one: 'project', many: 'projects' }, { searched: true })} in {plural(shownAreas.length, 'area')}</span> : null}
            </div>
          ) : null}
          {words.length && o.projects.length === 0 && shownAreas.length === 0 ? <NoMatch tone={tone} icon={<Folder />} q={search.typed.trim()} noun="projects or areas" onClear={search.clear} /> : null}

          {shownAreas.map((a) => (
            <PartBoundary key={a.id} name={a.name} tone={areaTone(a.id)}>
              <AreaSection area={a} overview={o} onNew={() => setCreating({ area: a.id })} onEdit={() => setEditing(a)} onMove={setMoving} canMove={mayMove} />
            </PartBoundary>
          ))}

          {unfiled.length ? (
            <section className="list-group-section" aria-label="Not in an area">
              <SectionHeading title="Not in an area" count={unfiled.length} description="Registered from workbenches, or made without an area. A portfolio manager files them, or their owners ask." />
              <ProjectGrid cards={unfiled.slice(0, unfiledDrawn)} overview={o} actions={{ onMove: setMoving, canMove: mayMove }} showArea={false} />
              {unfiled.length > unfiledDrawn ? <ShowMore onPress={() => setUnfiledDrawn((n) => n + AREA_PAGE)} /> : null}
            </section>
          ) : null}

          <PartBoundary key="managers" name="Portfolio managers" tone={tone}>
            <PortfolioManagersGroup onChange={load.reload} />
          </PartBoundary>

          <ListGroup>
            <ListRow to="/teams" icon={<Persons />} title="Teams and specialists" description="Built from catalogue roles and skills and granted to projects; they have a page of their own." />
          </ListGroup>

          {creating ? <NewProjectDialog isOpen onClose={() => setCreating(undefined)} overview={o} {...(creating.area ? { area: creating.area } : {})} /> : null}
          <MoveProjectDialog card={moving} overview={o} onClose={() => setMoving(undefined)} onMoved={load.reload} />
          {newArea ? <AreaDialog onClose={() => setNewArea(false)} onSaved={load.reload} portfolioManager /> : null}
          {editing ? <AreaDialog area={editing} onClose={() => setEditing(undefined)} onSaved={load.reload} portfolioManager={o.can.portfolioManager} /> : null}
        </>
      ) : null}
    </Page>
  );
}

const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * Who manages the whole portfolio, named here (`/portfolio/v1/managers`): people, or directory groups whose members all
 * manage it, so nobody is assigned one by one. Administrators change it; everyone sees it. The Entra app role still counts.
 */
function PortfolioManagersGroup({ onChange }: { onChange: () => void }) {
  const list = useLoad(() => routes.portfolioManagers(), []);
  const admin = useMay('organisation.settings', 'manage');
  const step = useStepUp();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const managers = list.data?.managers ?? [];
  const save = async (next: AreaManager[]) => {
    setError(undefined);
    setBusy(true);
    try {
      await step(() => routes.setPortfolioManagers(next), 'Sign in again to change who manages the portfolio.');
      list.reload();
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (list.error) return <ErrorNote error={list.error} />;
  if (!list.data || (!admin && managers.length === 0)) return null;
  const groups = groupsAvailable();
  return (
    <ListGroup
      title="Portfolio managers"
      count={managers.length || undefined}
      description={`They manage every business area and own every project in them. ${groups ? 'Name a directory group and everyone in it is one; leaving the group ends it. ' : ''}People given the Portfolio manager role in Microsoft Entra are too.`}
    >
      {managers.map((m) => (
        <ListRow
          key={`${m.subjectType}/${m.subjectId}`}
          icon={<Persons />}
          title={<SubjectName id={m.subjectId} fallback={m.subjectType === 'group' ? 'A directory group' : undefined} />}
          description={m.subjectType === 'group' ? 'Group: everyone in it' : 'Person'}
          {...(admin
            ? { control: <RowMenu label="More for this portfolio manager" items={[{ id: 'remove', label: 'Remove as portfolio manager', danger: true }]} onAction={() => void save(managers.filter((x) => !(x.subjectType === m.subjectType && x.subjectId === m.subjectId)))} /> }
            : {})}
        />
      ))}
      {admin ? (
        <ListRow title="Add a portfolio manager" description={groups ? 'Find a person or a directory group.' : 'Find a person.'}>
          <SubjectPicker
            className="w-full"
            kinds={['user', 'group']}
            placeholder={groups ? 'A person or group by name' : 'A person by name'}
            onPick={(s) => !busy && void save([...managers.filter((x) => !(x.subjectType === s.type && x.subjectId === s.id)), { subjectType: s.type, subjectId: s.id }])}
          />
        </ListRow>
      ) : null}
      {error ? <ErrorNote error={error} /> : null}
    </ListGroup>
  );
}

/** Cards an area draws at a time; Show more draws this many more. */
const AREA_PAGE = 12;

/** One area: who manages it, a line of what it holds, then its projects as cards. */
function AreaSection({ area: a, overview: o, onNew, onEdit, onMove, canMove }: { area: ProjectOverviewArea; overview: ProjectOverview; onNew: () => void; onEdit: () => void; onMove: (c: ProjectOverviewCard) => void; canMove: (c: ProjectOverviewCard) => boolean }) {
  const cards = o.projects.filter((p) => p.area?.id === a.id);
  const [drawn, setDrawn] = useState(AREA_PAGE);
  const tone = areaTone(a.id);
  const managers = a.managers.length ? (
    <>
      Managed by{' '}
      {a.managers.map((m, i) => (
        <span key={m.subjectId}>
          {i > 0 && (i === a.managers.length - 1 ? ' and ' : ', ')}
          {m.name ?? <SubjectName id={m.subjectId} fallback="a group" />}
        </span>
      ))}
    </>
  ) : (
    'Portfolio managers look after it'
  );
  const needs = a.attention.filing + a.attention.projects;
  // What the area holds, in one line of words: its projects, the people on them, its month's spend, its cost code.
  const facts = cards.length ? [plural(a.projects, 'project'), `${plural(a.people, 'person', 'people')} on them`, a.spendUsd ? `${formatMoney(a.spendUsd, o.money, { compact: true })} in 30 days` : '', a.costCode ? `cost code ${a.costCode}` : ''].filter(Boolean).join(' · ') : '';
  const attention = needs ? [a.attention.filing ? plural(a.attention.filing, 'filing request') : '', a.attention.projects ? `${plural(a.attention.projects, 'project')} failing or blocked` : ''].filter(Boolean).join(', ') : '';
  return (
    <section className="list-group-section" aria-label={a.name}>
      <SectionHeading
        title={a.name}
        count={cards.length}
        description={
          <>
            <span className="block">
              {managers}
              {a.description ? `. ${a.description}` : '.'}
            </span>
            {facts ? <span className="block tabular-nums">{facts}</span> : null}
            {a.mine && cards.length ? (
              <span className="mt-1 block">{needs ? <StatusMark state="medium" label={`${needs} ${needs === 1 ? 'thing needs' : 'things need'} a manager: ${attention}`} wrap /> : <StatusMark state="online" label="Nothing needs a manager" />}</span>
            ) : null}
          </>
        }
        actions={
          a.mine ? (
            <>
              <Button size="sm" variant="ghost" onPress={onEdit}>
                Edit
              </Button>
              <Button size="sm" variant="secondary" onPress={onNew}>
                <Plus className="size-4" />
                Project
              </Button>
            </>
          ) : undefined
        }
      />
      {cards.length ? (
        <>
          <ProjectGrid cards={cards.slice(0, drawn)} overview={o} actions={{ onMove, canMove }} showArea={false} />
          {cards.length > drawn ? <ShowMore onPress={() => setDrawn((n) => n + AREA_PAGE)} /> : null}
        </>
      ) : (
        <ArtEmpty compact tone={tone} icon={<Folder />} title={`No projects in ${a.name} yet`} action={a.mine ? <Button size="sm" variant="secondary" onPress={onNew}>New project</Button> : undefined}>
          {a.mine ? 'Start one here, or accept a project whose owners ask to file it under this area.' : 'Its managers file projects here, or a project’s owners ask to.'}
        </ArtEmpty>
      )}
    </section>
  );
}

/** Projects asking to be filed under an area: accepted or rejected by its managers here, withdrawn by whoever asked. */
function FilingRequests({ overview: o, onChange }: { overview: ProjectOverview; onChange: () => void }) {
  return (
    <ListGroup title="Filing requests" count={o.promotions.length} description="Accepting files the project under the area, whose managers then own it with its members.">
      {o.promotions.map((p) => (
        <FilingRow key={p.id} promotion={p} onChange={onChange} />
      ))}
    </ListGroup>
  );
}

function FilingRow({ promotion: p, onChange }: { promotion: ProjectOverviewPromotion; onChange: () => void }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const decide = async (d: 'accept' | 'reject' | 'withdraw') => {
    setBusy(d);
    setError(undefined);
    try {
      await routes.decidePromotion(p.id, d, d === 'reject' && reason.trim() ? reason.trim() : undefined);
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <ListRow
      icon={<ArrowUpFromLine />}
      title={
        <>
          <Link to={`/projects/${encodeURIComponent(p.projectId)}`} className="text-foreground hover:underline">
            {p.projectName ?? 'A project'}
          </Link>
          <span className="font-normal text-muted"> into </span>
          {p.areaName ?? 'an area'}
        </>
      }
      description={
        <>
          Asked by {p.requestedByName ?? <SubjectName id={p.requestedBy} fallback="its owner" />} {timeAgo(p.requestedAt)}
          {p.note ? ` · “${p.note}”` : ''}
        </>
      }
      control={
        p.decides ? (
          <>
            <Button size="sm" variant="ghost" onPress={() => setRejecting(!rejecting)}>
              Reject…
            </Button>
            <Button size="sm" variant="secondary" isPending={busy === 'accept'} onPress={() => void decide('accept')}>
              Accept
            </Button>
          </>
        ) : (
          <Button size="sm" variant="secondary" isPending={busy === 'withdraw'} onPress={() => void decide('withdraw')}>
            Withdraw
          </Button>
        )
      }
    >
      {rejecting || error ? (
        <div className="grid gap-2">
          {rejecting ? (
            <div className="flex flex-wrap items-center gap-2">
              <input className={`${inputClass} min-w-0 max-w-md flex-1 basis-56`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why, for the owner (optional)" aria-label="Why it was rejected" />
              <Button size="sm" variant="danger-soft" isPending={busy === 'reject'} onPress={() => void decide('reject')}>
                Reject
              </Button>
            </div>
          ) : null}
          <ErrorNote error={error} />
        </div>
      ) : null}
    </ListRow>
  );
}

const slug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);

/** A new business area, or one being edited: its name, a sentence, and (for a portfolio manager) its managers and cost code. */
function AreaDialog({ area, onClose, onSaved, portfolioManager }: { area?: ProjectOverviewArea; onClose: () => void; onSaved: () => void; portfolioManager: boolean }) {
  const [name, setName] = useState(area?.name ?? '');
  const [description, setDescription] = useState(area?.description ?? '');
  const [costCode, setCostCode] = useState(area?.costCode ?? '');
  const [managers, setManagers] = useState<AreaManager[]>(area?.managers.map((m) => ({ subjectType: m.subjectType, subjectId: m.subjectId })) ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await f();
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const save = () =>
    run(async () => {
      if (!area) {
        await routes.createArea({ id: slug(name), name: name.trim(), ...(description.trim() ? { description: description.trim() } : {}), managers });
        if (costCode.trim()) await routes.setAreaCostCode(slug(name), costCode.trim());
        return;
      }
      await routes.updateArea(area.id, { name: name.trim(), description: description.trim(), ...(portfolioManager ? { managers } : {}) });
      if (portfolioManager && costCode.trim() !== (area.costCode ?? '')) await routes.setAreaCostCode(area.id, costCode.trim());
    });
  const empty = area ? area.projects === 0 : false;
  return (
    <FormModal
      isOpen
      onClose={onClose}
      title={area ? `Edit ${area.name}` : 'New business area'}
      blurb={area ? undefined : 'A part of the organisation that owns projects: its managers file them, decide filing requests and see its spend.'}
      submitLabel={area ? 'Save' : 'Create area'}
      canSubmit={Boolean(slug(name))}
      busy={busy}
      error={error}
      onSubmit={() => void save()}
    >
      <label className="grid gap-1 text-xs text-muted">
        Name
        <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} placeholder="Finance" autoFocus maxLength={200} />
      </label>
      <label className="grid gap-1 text-xs text-muted">
        What it looks after (optional)
        <input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Billing, ledgers and payments" maxLength={2000} />
      </label>
      {portfolioManager ? (
        <>
          <label className="grid gap-1 text-xs text-muted">
            Cost code (optional)
            <input className={`${inputClass} max-w-xs`} value={costCode} onChange={(e) => setCostCode(e.target.value)} placeholder="The code finance books it to" maxLength={64} />
          </label>
          <div className="grid gap-1 text-xs text-muted">
            Managers
            {managers.length > 0 && (
              <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-1 p-0 text-sm text-foreground">
                {managers.map((m) => (
                  <li key={m.subjectId} className="inline-flex items-center gap-1">
                    <SubjectName id={m.subjectId} fallback={area?.managers.find((x) => x.subjectId === m.subjectId)?.name} />
                    <button type="button" className="cursor-[var(--cursor-interactive)] text-muted hover:text-foreground" aria-label="Remove this manager" onClick={() => setManagers(managers.filter((x) => x.subjectId !== m.subjectId))}>
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <SubjectPicker kinds={['user', 'group']} placeholder={groupsAvailable() ? 'Add a person or group' : 'Add a person'} onPick={(s) => setManagers([...managers.filter((m) => m.subjectId !== s.id), { subjectType: s.type === 'group' ? 'group' : 'user', subjectId: s.id }])} />
          </div>
        </>
      ) : null}
      {area && portfolioManager ? (
        <div className="flex items-center justify-between gap-3 border-t border-separator pt-3 text-xs text-muted">
          <span>{empty ? 'It holds no projects, so it can go.' : 'An area that holds projects cannot be deleted: move them first.'}</span>
          <Button size="sm" variant="danger-soft" isDisabled={!empty || busy} onPress={() => void run(() => routes.deleteArea(area.id))}>
            Delete area
          </Button>
        </div>
      ) : null}
    </FormModal>
  );
}
